// CashoutPage.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from './AppContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getDatabase, ref, get, onValue, off } from 'firebase/database';


export default function CashoutPage() {
  const {
    currentUser,
    userId2,          // needed for earnedCreditsByDate path
    creditsEarned,    // current credit balance (may be string/number)
    datWallet,        // per-block wallet used to compute rewards in $
  } = useContext(AppContext);

  const functions = getFunctions();

  const [payoutAmount, setPayoutAmount] = useState('');
  const [loadingCashOut, setLoadingCashOut] = useState(false);

  const [loadingStripeAccount, setLoadingStripeAccount] = useState(true);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [onboardingUrl, setOnboardingUrl] = useState(null);

  const [daysDelay, setDaysDelay] = useState(4);
  const [eligibleCredits, setEligibleCredits] = useState(0); // credits that have cleared delay window

  const [cashoutSource, setCashoutSource] = useState('credits'); // 'credits' | 'tokens'
const [poolCents, setPoolCents] = useState(0);
const [circulating, setCirculating] = useState(0);
const [userTokenQty, setUserTokenQty] = useState(0);


  /* ---------- helpers ---------- */
  const toNum = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v === 'string') return Number(v.replace(/[^0-9.-]/g, '')) || 0;
    if (typeof v === 'object') return toNum(Object.values(v)[0]);
    return 0;
  };
  const fmtUSD = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });



  useEffect(() => {
  const db = getDatabase();
  const poolRef = ref(db, 'metrics/datToken/poolCents');
  const circRef = ref(db, 'metrics/datToken/circulating');
  const qtyRef  = ref(db, `datChainTokenWallets/${userId2}/qty`);

  const u1 = onValue(poolRef, s => setPoolCents(Number(s.val() || 0)), () => setPoolCents(0));
  const u2 = onValue(circRef, s => setCirculating(Number(s.val() || 0)), () => setCirculating(0));
  const u3 = onValue(qtyRef,  s => setUserTokenQty(Number(s.val() || 0)), () => setUserTokenQty(0));

  return () => {
    off(poolRef, 'value', u1);
    off(circRef, 'value', u2);
    off(qtyRef,  'value', u3);
  };
}, [userId2]);


  /* ---------- compute eligible credits using RN logic ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) delay days from Firestore
        const cfgSnap = await getDoc(doc(getFirestore(), 'TimeToPayout', 'config'));
        const delay   = cfgSnap.exists() ? toNum(cfgSnap.data()?.days) : 4;
        if (!cancelled) setDaysDelay(delay || 4);

        // 2) cutoff date (YYYY-MM-DD)
        const cutoff = new Date(Date.now() - (delay || 4) * 86_400_000).toISOString().slice(0, 10);

        // 3) read daily earned credits from RTDB
        let eligible = 0;
        if (userId2) {
          const dailySnap = await get(ref(getDatabase(), `users/${userId2}/summary/earnedCreditsByDate`));
          const dailyVal  = dailySnap.val() || {};
          Object.entries(dailyVal).forEach(([date, rec]) => {
            // cleared if the record date is on/before cutoff
            if (date <= cutoff) {
              const total = toNum(rec?.total);
              const spent = toNum(rec?.spent);
              const diff  = total - spent;
              if (diff > 0) eligible += diff;
            }
          });
        }

        // 4) safety cap: never exceed what the user actually has recorded
        const actualCredits = toNum(creditsEarned);
        if (eligible === 0 && actualCredits > 0) {
          eligible = actualCredits;
        } else if (eligible > actualCredits) {
          eligible = actualCredits;
        }

        if (!cancelled) setEligibleCredits(eligible);
      } catch (e) {
        console.error('[cashout] failed to compute eligible credits', e);
        // graceful fallback to current balance
        setEligibleCredits(toNum(creditsEarned));
      }
    })();
    return () => { cancelled = true; };
  }, [userId2, creditsEarned]);





  const spotUsd = useMemo(() => {
  const c = Number(circulating) || 0;
  const p = Number(poolCents) || 0;
  if (!c) return 0;
  return (p / 100) / c; 
}, [poolCents, circulating]);

const creditsUsd = useMemo(() => toNum(eligibleCredits) * 0.10, [eligibleCredits]);
const tokensUsd  = useMemo(() => +(((Number(userTokenQty) || 0) * spotUsd).toFixed(2)), [userTokenQty, spotUsd]);

const selectedAvailableUsd = cashoutSource === 'credits' ? creditsUsd : tokensUsd;


  /* ---------- Stripe account fetch ---------- */
  useEffect(() => {
    const fetchStripeAccount = async () => {
      setLoadingStripeAccount(true);
      try {
        const createOrRetrieveAccount = httpsCallable(functions, 'createOrRetrieveAccount');
        const resp = await createOrRetrieveAccount({ email: currentUser?.email });
        setStripeAccountId(resp?.data?.stripeAccountId || null);

        if (!resp?.data?.payouts_enabled) {
          const onboardingLink = httpsCallable(functions, 'createStripeOnboardingLink');
          const linkResp = await onboardingLink();
          setOnboardingUrl(linkResp?.data?.url || null);
        } else {
          setOnboardingUrl(null);
        }
      } catch (e) {
        console.error('Error fetching Stripe account:', e);
      } finally {
        setLoadingStripeAccount(false);
      }
    };

    fetchStripeAccount();
  }, [currentUser?.email, functions]);

  /* ---------- handle payout ---------- */
  const handleCashOut = async () => {
    const minimumPayoutAmount = 25;
    const amt = parseFloat(payoutAmount);

    if (!Number.isFinite(amt)) {
      alert('Enter a valid payout amount.');
      return;
    }
    if (amt < minimumPayoutAmount) {
      alert(`Minimum payout amount is $${minimumPayoutAmount.toFixed(2)}`);
      return;
    }
if (amt > selectedAvailableUsd + 0.0001) {
  alert('Amount exceeds your available balance.');
  return;
}


    setLoadingCashOut(true);
    try {
const processStripePayout = httpsCallable(functions, 'processStripePayoutDatChainApps2');
const resp = await processStripePayout({ payoutAmount: amt, source: cashoutSource });
      alert(`Payout successful! Transfer ID: ${resp?.data?.transferId || 'N/A'}`);
      setPayoutAmount('');
    } catch (e) {
      console.error('Error processing payout:', e);
      alert('Failed to process payout. Please try again.');
    } finally {
      setLoadingCashOut(false);
    }
  };

  /* ---------- loading / onboarding states ---------- */
  if (loadingStripeAccount) {
    return (
      <div className="dc-cashout-wrap">
        <div className="dc-card">
          <div className="dc-title">Cash Out</div>
          <div className="dc-row">
            <div className="spinner" />
            <div className="dc-note">Loading your Stripe account…</div>
          </div>
        </div>
        {styles}
      </div>
    );
  }

  if (onboardingUrl) {
    const copyToClipboard = () => {
      const link = `spndat.com/userprofile/${currentUser?.username || ''}`;
      navigator.clipboard.writeText(link)
        .then(() => alert('Link copied to clipboard!'))
        .catch(err => console.error('Failed to copy:', err));
    };

    return (
      <div className="dc-cashout-wrap">
        <div className="dc-card">
          <div className="dc-title">Complete Your Stripe Setup</div>
          <div className="dc-note">
            Enter <strong>spndat.com/userprofile/{currentUser?.username || 'your-username'}</strong> in the “Your website” field.
          </div>
          <div className="dc-actions">
            <button className="dc-btn ghost" onClick={copyToClipboard}>Copy Profile Link</button>
            <button
              className="dc-btn"
              onClick={() => { window.location.href = onboardingUrl; }}
            >
              Complete Setup
            </button>
          </div>
        </div>
        {styles}
      </div>
    );
  }

  return (
    <div className="dc-cashout-wrap">
      <div className="dc-card">
        <div className="dc-title">Cash Out</div>

        <div className="dc-actions" style={{ marginTop: 0 }}>
  <button
    className="dc-btn ghost"
    onClick={() => setCashoutSource('credits')}
    style={{ opacity: cashoutSource==='credits' ? 1 : 0.7 }}
  >
    Earnings
  </button>
  <button
    className="dc-btn ghost"
    onClick={() => setCashoutSource('tokens')}
    style={{ opacity: cashoutSource==='tokens' ? 1 : 0.7 }}
  >
    DAT Chain Tokens
  </button>
</div>


        <div className="dc-metrics">
<div className="dc-metric">
  <div className="dc-label">Earnings</div>
  <div className="dc-value">${fmtUSD(creditsUsd)}</div>
</div>
<div className="dc-metric">
  <div className="dc-label">DAT Chain Tokens</div>
  <div className="dc-value">${fmtUSD(tokensUsd)}</div>
</div>
<div className="dc-metric">
  <div className="dc-label">Available</div>
  <div className="dc-value">${fmtUSD(selectedAvailableUsd)}</div>
</div>


          <div className="dc-metric">
            <div className="dc-label">Stripe Account</div>
            <div className="dc-value mono">{stripeAccountId ? 'Connected' : 'Not Connected'}</div>
          </div>
        </div>

        <div className="dc-input-row">
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Enter payout amount (USD)"
            value={payoutAmount}
onChange={(e) => {
  const raw = e.target.value;
  const cleaned = String(raw).replace(/[^0-9.]/g, '').replace(/^0+(?!\.|$)/, '');
  const val = parseFloat(cleaned) || 0;
  const max = Number(selectedAvailableUsd.toFixed(2));
  setPayoutAmount(val > max ? String(max) : cleaned);
}}

            className="dc-input"
            disabled={loadingCashOut}
          />
          <button
            className="dc-btn ghost"
onClick={() => setPayoutAmount(selectedAvailableUsd.toFixed(2))}

            disabled={loadingCashOut}
          >
            Max
          </button>
        </div>

        {/* DAT Chain–styled primary button */}
        <div className="dc-cashout-button">
          <button
            className="dc-btn big"
            onClick={handleCashOut}
            disabled={loadingCashOut}
          >
            {loadingCashOut ? 'Processing…' : 'Cash Out'}
          </button>
        </div>

        {loadingCashOut && (
          <div className="dc-row" style={{ marginTop: 10 }}>
            <div className="spinner" />
            <div className="dc-note">Processing payout…</div>
          </div>
        )}
      </div>

      {styles}
    </div>
  );
}

/* Inline styles / theme */
const styles = (
  <style>{`
    :root{
      --brand-top:#fe24ad;
      --brand-bot:#a410e8;
      --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
      --brand-top-20:rgba(254,36,173,.2);
      --brand-bot-40:rgba(164,16,232,.4);
    }
    *{margin:0;padding:0;box-sizing:border-box;}
    html,body{font-family:sans-serif;background:#000;color:#fff;}
    .homepage-container{min-height:100vh;padding:2rem;display:flex;flex-direction:column;}

    .dc-cashout-wrap{
      min-height:100vh;
      display:flex;
      align-items:center;
      justify-content:center;
      padding:2rem;
      background:
        radial-gradient(700px 500px at 20% 15%, var(--brand-top-20), transparent 60%),
        radial-gradient(600px 400px at 80% 85%, var(--brand-bot-40), transparent 60%),
        #000;
      color:#fff;
      font-family:sans-serif;
    }
    .dc-card{
      width:100%;
      max-width:680px;
      background:#050008;
      border:1px solid var(--brand-bot-40);
      border-radius:14px;
      box-shadow:0 10px 30px rgba(0,0,0,.45);
      padding:1.25rem 1.25rem 1rem;
    }
    .dc-title{
      font-size:1.4rem;
      font-weight:900;
      margin-bottom:.6rem;
      background:var(--brand-grad);
      -webkit-background-clip:text;
      -webkit-text-fill-color:transparent;
    }
    .dc-note{ color:rgba(255,255,255,.85); line-height:1.35; }
    .dc-actions{ display:flex; gap:.6rem; margin-top:1rem; flex-wrap:wrap; }

    .dc-metrics{
      display:grid;
      grid-template-columns:repeat(auto-fit,minmax(220px,1fr));
      gap:.8rem;
      margin:.8rem 0 1rem;
    }
    .dc-metric{
      background:#09020f;
      border:1px solid rgba(255,255,255,.12);
      border-radius:10px;
      padding:.8rem;
    }
    .dc-label{
      font-size:.85rem;
      color:rgba(255,255,255,.8);
      margin-bottom:.25rem;
    }
    .dc-value{
      font-size:1.1rem;
      font-weight:800;
      background:var(--brand-grad);
      -webkit-background-clip:text;
      -webkit-text-fill-color:transparent;
    }
    .mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace; }

    .dc-input-row{ display:flex; gap:.6rem; margin:.2rem 0 1rem; }
    .dc-input{
      flex:1;
      padding:.7rem .85rem;
      border:1px solid var(--brand-bot-40);
      border-radius:10px;
      background:#0b0611;
      color:#fff;
      font-size:1rem;
    }
    .dc-input:focus{
      outline:none;
      border-color:var(--brand-top);
      box-shadow:0 0 6px var(--brand-top);
    }

    .dc-btn{
      padding:.65rem 1.1rem;
      border:none;
      border-radius:10px;
      font-weight:800;
      cursor:pointer;
      background:var(--brand-grad);
      color:#fff;
      transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
      box-shadow:0 4px 12px rgba(164,16,232,.25);
    }
    .dc-btn.big{ padding:.85rem 1.25rem; font-size:1.05rem; min-width:180px; }
    .dc-btn:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(164,16,232,.35); }
    .dc-btn:active{ transform:translateY(0); }
    .dc-btn.ghost{
      background:transparent;
      color:#fff;
      border:1px solid rgba(255,255,255,.25);
      box-shadow:none;
    }
    .dc-btn:disabled{ opacity:.6; cursor:not-allowed; transform:none; box-shadow:none; }

    .dc-cashout-button{ display:flex; justify-content:center; margin-top:.25rem; }

    .dc-row{ display:flex; align-items:center; gap:.6rem; }
    .spinner{
      width:24px; height:24px;
      border:3px solid rgba(255,255,255,.15);
      border-top-color:var(--brand-top);
      border-radius:50%;
      animation:spin 1s linear infinite;
    }
    @keyframes spin{ to{ transform:rotate(360deg); } }
  `}</style>
);
